import type { PushTestResult } from '../types/push';

/**
 * A push teszt eredményének kiírása a böngésző konzoljára.
 *
 * Miért kell ehhez külön modul: ha egy értesítés nem érkezik meg, az ok a lánc
 * bármelyik pontján lehet - a böngésző engedélyénél, a service workernél, a
 * feliratkozásnál, a szerver VAPID beállításánál vagy magánál a push
 * szolgáltatónál. A toast ebből egy mondatot tud mutatni, ezért a teljes képet
 * a konzolra írjuk ki, mindkét oldalról összeszedve.
 */

const LOG_PREFIX = '[Portero push teszt]';

interface ClientDiagnostics {
  /** A böngésző értesítési engedélye: 'granted' | 'denied' | 'default'. */
  notificationPermission: string;
  /** Támogatja-e a böngésző egyáltalán a Push API-t. */
  pushApiSupported: boolean;
  serviceWorkerSupported: boolean;
  /** Biztonságos kontextus (HTTPS vagy localhost) - enélkül nincs push. */
  secureContext: boolean;
  serviceWorkerState: string;
  serviceWorkerScope: string | null;
  /** Van-e élő feliratkozás ebben a böngészőben. */
  hasSubscription: boolean;
  subscriptionService: string | null;
  /** Az endpoint rövidítve - ennyi elég a szerveroldali eszközzel való összevetéshez. */
  subscriptionEndpointHint: string | null;
  subscriptionExpires: string | null;
  /** Telepített PWA-ként fut-e. iOS-en csak így működik a push. */
  standalone: boolean;
  userAgent: string;
}

/**
 * Az endpointot nem írjuk ki teljes egészében: az gyakorlatilag titok, aki
 * ismeri, tud rá értesítést küldeni - a konzol tartalma pedig könnyen
 * screenshotra vagy hibajegyre kerül.
 */
const hintEndpoint = (endpoint: string): string => {
  try {
    const url = new URL(endpoint);
    return `${url.host}/…${endpoint.slice(-8)}`;
  } catch {
    return `…${endpoint.slice(-8)}`;
  }
};

const describeServiceWorker = (registration: ServiceWorkerRegistration | undefined): string => {
  if (!registration) return 'nincs regisztrálva';
  if (registration.active) return `aktív (${registration.active.state})`;
  if (registration.waiting) return 'várakozik (waiting)';
  if (registration.installing) return 'telepítés alatt (installing)';
  return 'regisztrálva, de nincs worker';
};

export const collectClientDiagnostics = async (): Promise<ClientDiagnostics> => {
  const serviceWorkerSupported = 'serviceWorker' in navigator;
  const pushApiSupported = 'PushManager' in window;

  let registration: ServiceWorkerRegistration | undefined;
  let subscription: PushSubscription | null = null;

  if (serviceWorkerSupported) {
    try {
      registration = await navigator.serviceWorker.getRegistration();
      subscription = registration ? await registration.pushManager.getSubscription() : null;
    } catch {
      // A diagnosztika ne bukjon el azon, hogy épp nem elérhető a worker
    }
  }

  return {
    notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'nincs Notification API',
    pushApiSupported,
    serviceWorkerSupported,
    secureContext: window.isSecureContext,
    serviceWorkerState: describeServiceWorker(registration),
    serviceWorkerScope: registration?.scope ?? null,
    hasSubscription: subscription !== null,
    subscriptionService: subscription ? new URL(subscription.endpoint).host : null,
    subscriptionEndpointHint: subscription ? hintEndpoint(subscription.endpoint) : null,
    subscriptionExpires:
      subscription?.expirationTime != null ? new Date(subscription.expirationTime).toISOString() : null,
    standalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true,
    userAgent: navigator.userAgent,
  };
};

/** Sikeres vagy részben sikeres teszt kiírása. */
export const logPushTestResult = async (result: PushTestResult): Promise<void> => {
  const client = await collectClientDiagnostics();
  const { result: delivery } = result;

  const headline = result.success
    ? `${LOG_PREFIX} SIKERES - ${delivery.delivered}/${delivery.subscriptionsTargeted} eszköz`
    : `${LOG_PREFIX} SIKERTELEN - egyetlen eszközre sem ment ki`;

  // Sikertelen esetben csoportot nyitunk kibontva, hogy ne kelljen kattintani érte
  const openGroup = result.success ? console.groupCollapsed : console.group;
  openGroup.call(console, `%c${headline}`, `color: ${result.success ? '#16a34a' : '#dc2626'}; font-weight: bold`);

  if (!result.success) {
    console.error(`${LOG_PREFIX} Ok:`, result.reason ?? 'ismeretlen');
  }

  console.log('Küldés ideje (UTC):', result.sentAt);
  console.log('Elküldött tartalom:', result.payload);

  console.group('Böngésző oldal');
  console.table(client);
  console.groupEnd();

  console.group('Szerver oldal');
  console.table({
    'VAPID kulcs beállítva': result.vapidConfigured,
    'Fiók szintű push engedélyezve': result.accountPushEnabled,
    'Feliratkozott saját eszköz': result.ownDevices,
    'Megcélzott eszköz': delivery.subscriptionsTargeted,
    'Kézbesítve': delivery.delivered,
    'Hibás': delivery.failed,
    'Törölt (lejárt) feliratkozás': delivery.removed,
    'Kihagyva (nincs VAPID)': delivery.skipped,
  });
  console.groupEnd();

  if (delivery.devices.length > 0) {
    console.group(`Eszközönkénti eredmény (${delivery.devices.length})`);
    console.table(delivery.devices);
    delivery.devices
      .filter((device) => device.error)
      .forEach((device) =>
        console.error(
          `${LOG_PREFIX} ${device.deviceName ?? `#${device.subscriptionId}`} (${device.pushService ?? 'ismeretlen szolgáltató'})`,
          `- ${device.outcome}${device.statusCode ? ` [HTTP ${device.statusCode}]` : ''}: ${device.error}`
        )
      );
    console.groupEnd();
  } else {
    console.warn(`${LOG_PREFIX} A szerver egyetlen eszközre sem próbált küldeni.`);
  }

  console.log('Nyers szerver válasz:', result);
  console.groupEnd();
};

/**
 * Akkor hívjuk, ha a kérés el sem jutott a szerverig, vagy hibával tért vissza -
 * ilyenkor szerveroldali diagnosztika nincs, csak a böngésző oldali.
 */
export const logPushTestFailure = async (error: unknown, message: string): Promise<void> => {
  const client = await collectClientDiagnostics();

  console.group(`%c${LOG_PREFIX} SIKERTELEN - a kérés nem ért célba`, 'color: #dc2626; font-weight: bold');
  console.error(`${LOG_PREFIX} Ok:`, message);
  console.group('Böngésző oldal');
  console.table(client);
  console.groupEnd();
  console.error('Nyers hiba:', error);
  console.groupEnd();
};
